/* Kitsters US - retail cart drawer behaviour (Dawn-native).
 *
 * The drawer's MARKUP is snippets/cart-drawer.liquid and its LOOK is
 * assets/kitsters-cart.css (the India drawer design, ported). Dawn's own
 * cart.js / cart-drawer.js keep owning quantity, remove, note autosave and
 * section re-rendering - this file adds only the three behaviours the India
 * drawer had on top of its platform: the TOTAL accordion, the discount-code
 * box, and the upsell rail's one-tap add.
 *
 * Everything is DELEGATED to document: the drawer re-renders its whole
 * innerHTML on every cart change (Dawn's section rendering), so element-bound
 * listeners would die on the first add.
 *
 * India lessons carried over rather than relearned:
 *  - /cart/update.js only matches DISCOUNT codes. A gift card typed here would
 *    come back "applicable: false" and a generic error calls the customer's
 *    valid gift card invalid (root CLAUDE.md 10.74) - a gift-card-shaped code
 *    gets the honest message from data-giftcard-message instead.
 *  - Never blank the typed code on rejection (10.29) - retyping 16 characters
 *    on a phone is the punishment for a typo.
 *  - A failed count/refresh must never break the flow - every network path
 *    catches and degrades.
 */
(function () {
  'use strict';

  var applying = false;

  function drawerReload() {
    if (window.KitstersCart && window.KitstersCart.reload) {
      window.KitstersCart.reload();
      return;
    }
    /* Bridge missing (script blocked?) - full reload beats a stale drawer. */
    window.location.reload();
  }

  function giftCardShaped(code) {
    var c = (code || '').replace(/[\s-]/g, '');
    return c.length >= 12 && /[a-z]/i.test(c) && /\d/.test(c);
  }

  function showError(box, code) {
    var err = box.querySelector('[data-ktc-discount-error]');
    if (!err) return;
    if (giftCardShaped(code) && err.dataset.giftcardMessage) {
      err.textContent = err.dataset.giftcardMessage;
    }
    err.style.display = '';
  }

  function applyDiscount(box) {
    if (applying) return;
    var input = box.querySelector('[data-ktc-discount-input]');
    var code = input && input.value.trim();
    if (!code) return;
    applying = true;
    var btn = box.querySelector('[data-ktc-discount-apply]');
    if (btn) btn.disabled = true;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var codes = cart.discount_codes || [];
        var hit = codes.filter(function (c) {
          return c.code && c.code.toLowerCase() === code.toLowerCase() && c.applicable;
        })[0];
        if (hit) {
          drawerReload();
        } else {
          /* Clear the rejected code from the cart session or it lingers and
             re-errors on the next attempt. Keep the typed value (10.29). */
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: '' })
          }).catch(function () {});
          showError(box, code);
        }
      })
      .catch(function () { showError(box, code); })
      .finally(function () {
        applying = false;
        if (btn) btn.disabled = false;
      });
  }

  function removeDiscount() {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' })
    })
      .then(function () { drawerReload(); })
      .catch(function () {});
  }

  function upsellAdd(btn) {
    var id = parseInt(btn.getAttribute('data-ktc-upsell-add'), 10);
    if (!id || btn.disabled) return;
    btn.disabled = true;
    var label = btn.textContent;
    btn.textContent = '...';
    var add = window.KitstersCart && window.KitstersCart.addToCart
      ? window.KitstersCart.addToCart({ id: id, quantity: 1 })
      : fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: id, quantity: 1 })
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        }).then(function (d) { drawerReload(); return d; });
    add
      .then(function () { btn.textContent = 'ADDED ✓'; })
      .catch(function () {
        btn.textContent = label;
        btn.disabled = false;
      });
  }

  document.addEventListener('click', function (e) {
    var t = e.target;

    /* Close on a scrim click. Dawn binds close to #CartDrawer-Overlay, but
       that div lives INSIDE the panel, where it cannot cover the scrim - the
       scrim here is the cart-drawer root itself (the panel is its child, so a
       click on the panel never has the root as target). */
    if (t.matches && t.matches('cart-drawer.drawer.active')) {
      t.close();
      return;
    }

    var toggle = t.closest('[data-ktc-total-toggle]');
    if (toggle) {
      /* Tapping a link/button inside the box must not also toggle it. */
      if (!t.closest('a, [data-ktc-discount-apply]')) {
        var open = toggle.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      return;
    }

    var apply = t.closest('[data-ktc-discount-apply]');
    if (apply) {
      applyDiscount(apply.closest('[data-ktc-discount]'));
      return;
    }

    var remove = t.closest('[data-ktc-discount-remove]');
    if (remove) {
      removeDiscount();
      return;
    }

    var upsell = t.closest('[data-ktc-upsell-add]');
    if (upsell) upsellAdd(upsell);
  });

  /* Dawn's cart.js announces its own updates (qty change, remove inside the
     drawer) on its pub-sub channel, not on this theme's cart:count:update -
     without this bridge the header pill and favicon badge go stale the moment
     the shopper edits quantities IN the drawer. */
  /* constants.js declares PUB_SUB_EVENTS with a top-level `const`, which never
     becomes a window property - `window.PUB_SUB_EVENTS` is undefined even when
     the constant is live, so the guard must be typeof-based or the bridge
     silently never registers. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var count = event && event.cartData && typeof event.cartData.item_count === 'number'
        ? event.cartData.item_count : null;
      if (count === null) return;
      document.dispatchEvent(new CustomEvent('cart:count:update', {
        detail: { item_count: count }
      }));
    });
  }

  /* The empty-state "+ Add to Cart" cards and the gift-wrap band are plain
     product forms - un-intercepted they navigate the whole page to /cart.
     India's stay in the drawer; add through the bridge instead and let the
     section re-render refresh everything in place. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('.ktc-empty-atc-form, .ktc-giftwrap-form')) return;
    e.preventDefault();
    var idInput = form.querySelector('input[name="id"]');
    var id = idInput && parseInt(idInput.value, 10);
    if (!id) return;
    /* `submitter` first, and it is load-bearing for the gift wrap: that button
       is associated with its form by the `form` attribute and lives in a
       different part of the document (snippets/kitsters-gift-wrap-form.liquid
       explains why), so form.querySelector cannot reach it and the "..."
       feedback would be silently lost. The querySelector arm still serves the
       empty-state ATC cards, whose buttons really are inside their form. */
    var btn = e.submitter || form.querySelector('button[type="submit"]');
    var label = btn && btn.textContent;
    if (btn) { btn.disabled = true; btn.textContent = '...'; }
    var add = window.KitstersCart && window.KitstersCart.addToCart
      ? window.KitstersCart.addToCart({ id: id, quantity: 1 })
      : fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: id, quantity: 1 })
        }).then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        }).then(function (d) { drawerReload(); return d; });
    add.catch(function () {
      if (btn) { btn.disabled = false; btn.textContent = label; }
    });
  });

  /* ── Notification bar: rotating messages (India-parity yellow bar above the
     footer). The drawer re-renders its whole subtree on every cart change, so
     nothing is bound to the elements: each tick re-queries by id and no-ops
     when the bar is absent (empty cart, bar not in DOM). Copy is US-safe -
     brand voice and the computed savings line only, no review counts or
     India-store claims (US CLAUDE.md §3.4); the savings text is Liquid-
     rendered into data-ktc-bar-savings so this file never formats money. */
  var BAR_MESSAGES = [
    '🚚 Free shipping on all orders',
    '✨ Make Everyday Magical',
    '💝 Made for you with love and magic'
  ];
  var barIndex = 0;
  function tickBar() {
    var bar = document.getElementById('ktc-cart-notification-bar');
    var text = document.getElementById('ktc-cart-bar-text');
    if (!bar || !text) return;
    var msgs = BAR_MESSAGES.slice();
    var savings = bar.getAttribute('data-ktc-bar-savings');
    if (savings) msgs.unshift(savings);
    barIndex = barIndex % msgs.length;
    text.textContent = msgs[barIndex];
    bar.classList.add('is-visible');
    text.classList.add('ktc-fade-visible');
    setTimeout(function () { text.classList.remove('ktc-fade-visible'); }, 2500);
    barIndex++;
  }
  tickBar();
  setInterval(tickBar, 3000);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var t = e.target;
    if (t.matches && t.matches('[data-ktc-total-toggle]')) {
      e.preventDefault();
      var open = t.classList.toggle('is-open');
      t.setAttribute('aria-expanded', open ? 'true' : 'false');
    } else if (t.matches && t.matches('[data-ktc-discount-input]') && e.key === 'Enter') {
      e.preventDefault();
      applyDiscount(t.closest('[data-ktc-discount]'));
    }
  });
})();
